import { RadioField } from "@overmap-ai/forms"
import type { ReactNode } from "react"

import {
	NullableStringEqualsFilterModifier,
	NullableStringNotEqualsFilterModifier,
	StringArrayExcludesFilterModifier,
	StringArrayIncludesFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { RadioFieldFilterCell } from "./RadioFieldFilterCell"

type RadioFieldValue = string | null
type RadioFieldFilterValue = string[] | string | null

export class RadioFieldFilterDef extends BaseFilterDef<RadioFieldValue, RadioFieldFilterValue> {
	readonly id: string
	readonly defaultFilterValue: RadioFieldFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly field: RadioField

	constructor(field: RadioField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableStringEqualsFilterModifier,
			isValueValid: (_value: RadioFieldValue): _value is string | null => true,
			isFilterValueValid: (filterValue: RadioFieldFilterValue): filterValue is string | null =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableStringNotEqualsFilterModifier,
			isValueValid: (_value: RadioFieldValue): _value is string | null => true,
			isFilterValueValid: (filterValue: RadioFieldFilterValue): filterValue is string | null =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: StringArrayIncludesFilterModifier,
			isValueValid: (value: RadioFieldValue): value is string => value !== null,
			isFilterValueValid: (filterValue: RadioFieldFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: StringArrayExcludesFilterModifier,
			isValueValid: (value: RadioFieldValue): value is string => value !== null,
			isFilterValueValid: (filterValue: RadioFieldFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<RadioFieldFilterDef>): ReactNode {
		return <RadioFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
